import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, SignIn, SignOut, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["pistol", "revolver", "shotgun", "rifle"];
const CONDITIONS = ["serviceable", "needs repair", "unserviceable"];
const EMPTY = {
  make: "", model: "", caliber: "", serial_no: "", firearm_type: "pistol",
  license_no: "", license_expiry: "", notes: "",
};

/**
 * The agency armoury.
 *
 * ⚠️ This screen exists because the register had none. Every route below was
 * built, guarded and provable, and a supervisor handing a firearm to a guard at
 * the start of a detail had no way to record it — which makes the register a
 * document nobody maintains, and a register nobody maintains is worse than no
 * register, because the office believes it.
 *
 * The firearm is an AGENCY asset under RA 10591, not a field on the guard, so
 * this is where custody changes hands. The guard's 201 file reads its half of
 * the chain from here.
 */
export default function Firearms() {
  const [arms, setArms] = useState([]);
  const [guards, setGuards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [issueFor, setIssueFor] = useState(null);
  const [issueForm, setIssueForm] = useState({ guard_id: "", rounds: 15, duty_detail_no: "", remarks: "" });
  const [returnFor, setReturnFor] = useState(null);
  const [returnForm, setReturnForm] = useState({ rounds_returned: 0, condition: "serviceable", remarks: "", discrepancy_reason: "" });
  const [sel, setSel] = useState(null);

  const load = () => {
    api.get("/firearms").then((r) => setArms(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/firearms/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/guards").then((r) => setGuards(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const today = todayStr();
  // A guard already signed for something cannot take a second unit, so they are
  // not offered. The server refuses it either way; the list simply should not
  // put a choice in front of somebody that it is going to reject.
  const holding = new Set(arms.filter((f) => f.status === "issued").map((f) => f.issued_to));
  const eligible = guards.filter(
    (g) => g.employment_status === "active" && g.deployable !== false && !holding.has(g.id)
  );

  const create = async () => {
    try {
      await api.post("/firearms", { ...form, license_expiry: form.license_expiry || null });
      toast.success("Firearm added to the register");
      setNewOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const issue = async () => {
    try {
      const { data } = await api.post(`/firearms/${issueFor.id}/issue`, {
        ...issueForm, rounds: Number(issueForm.rounds) || 0,
      });
      toast.success(`${data.serial_no} signed out to ${data.issued_to_name} with ${data.rounds_out} round(s)`);
      setIssueFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doReturn = async () => {
    try {
      const { data } = await api.post(`/firearms/${returnFor.id}/return`, {
        ...returnForm, rounds_returned: Number(returnForm.rounds_returned) || 0,
      });
      const short = (returnFor.rounds_out || 0) - (Number(returnForm.rounds_returned) || 0);
      toast.success(short
        ? `${data.serial_no} received — ${short} round(s) short, supervisors notified`
        : `${data.serial_no} received back, full count`);
      setReturnFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openReturn = (f) => {
    setReturnForm({ rounds_returned: f.rounds_out || 0, condition: "serviceable", remarks: "", discrepancy_reason: "" });
    setReturnFor(f);
  };

  const shortNow = returnFor ? (returnFor.rounds_out || 0) - (Number(returnForm.rounds_returned) || 0) : 0;

  const columns = [
    { key: "ref_no", label: "Unit", render: (f) => <span className="font-mono text-xs">{f.ref_no}</span> },
    { key: "make", label: "Make & model", render: (f) => (
      <div>
        <div className="font-semibold">{f.make} {f.model}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{f.firearm_type}</div>
      </div>
    ) },
    { key: "caliber", label: "Calibre", render: (f) => <span className="font-mono text-xs">{f.caliber}</span> },
    { key: "serial_no", label: "Serial no.", render: (f) => <span className="font-mono text-xs font-bold">{f.serial_no}</span> },
    { key: "status", label: "Status", render: (f) => <StatusBadge value={f.status} /> },
    { key: "issued_to_name", label: "Issued to", render: (f) => f.issued_to_name || <span className="text-muted-foreground">—</span> },
    { key: "rounds_out", label: "Rounds", render: (f) => (
      f.status === "issued"
        ? <span className="font-mono text-xs font-bold">{f.rounds_out}</span>
        : <span className="text-muted-foreground">—</span>
    ) },
    { key: "license_expiry", label: "Registration", render: (f) => {
      const lapsed = f.license_expiry && f.license_expiry < today;
      return (
        <div className={lapsed ? "text-[hsl(var(--alert))] font-bold" : ""}>
          <div className="font-mono text-[11px]">{f.license_no || "—"}</div>
          <div className="font-mono text-[10px]">{f.license_expiry || "no expiry"}{lapsed ? " · LAPSED" : ""}</div>
        </div>
      );
    } },
    { key: "actions", label: "", render: (f) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {f.status === "in_armory" && (
          <Button size="sm" className="rounded-none h-7 text-[11px]" data-testid={`issue-${f.serial_no}`}
                  onClick={() => { setIssueForm({ guard_id: "", rounds: f.firearm_type === "shotgun" ? 5 : 15, duty_detail_no: "", remarks: "" }); setIssueFor(f); }}>
            <SignOut size={13} className="mr-1" /> Issue
          </Button>
        )}
        {f.status === "issued" && (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" data-testid={`return-${f.serial_no}`}
                  onClick={() => openReturn(f)}>
            <SignIn size={13} className="mr-1" /> Receive
          </Button>
        )}
      </div>
    ) },
  ];

  return (
    <div data-testid="page-firearms">
      <PageHeader
        title="Armoury"
        subtitle="Every firearm on the agency licence, who is carrying it, and what came back"
        action={<Button className="rounded-none" data-testid="new-firearm" onClick={() => setNewOpen(true)}>Add a firearm</Button>}
      />

      {summary && (
        <div className="grid md:grid-cols-4 gap-3 mb-5">
          <Tile label="On the register" value={summary.total} />
          <Tile label="Out on duty" value={summary.by_status?.issued || 0} />
          <Tile label="Rounds signed out" value={summary.rounds_out} />
          <Tile label="In the armoury" value={summary.by_status?.in_armory || 0} />
        </div>
      )}

      {/* Two conditions that cost an agency its licence, named rather than left
          for somebody to spot in a list. */}
      {summary?.registration_lapsed?.length > 0 && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-3 flex gap-2" data-testid="firearms-lapsed">
          <Warning size={18} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {summary.registration_lapsed.length} firearm registration{summary.registration_lapsed.length > 1 ? "s have" : " has"} lapsed.
            </span>{" "}
            {summary.registration_lapsed.map((x) => `${x.serial_no} (${x.expiry})`).join(", ")}.
            These cannot be issued until they are renewed — the risk sits with the agency licence, not the guard.
          </div>
        </div>
      )}
      {summary?.held_by_separated_guards?.length > 0 && (
        <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-3 flex gap-2" data-testid="firearms-stranded">
          <Warning size={18} weight="fill" className="text-[hsl(var(--alert))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {summary.held_by_separated_guards.length} firearm(s) are signed out to somebody who has left.
            </span>{" "}
            {summary.held_by_separated_guards.map((x) => `${x.serial_no} — ${x.issued_to_name} (${x.status_of_holder})`).join("; ")}.
            Recover them and receive them back here.
          </div>
        </div>
      )}

      <DataTable columns={columns} rows={arms} onRowClick={setSel} testid="firearms-table"
                 empty="No firearms on the register yet." />

      {/* ---- register a unit ------------------------------------------------ */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">Add a firearm to the register</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Make" v={form.make} set={(v) => setForm({ ...form, make: v })} ph="Armscor" testid="fa-make" />
            <Field label="Model" v={form.model} set={(v) => setForm({ ...form, model: v })} ph="M1911A1 GI Standard" testid="fa-model" />
            <Field label="Calibre" v={form.caliber} set={(v) => setForm({ ...form, caliber: v })} ph=".45 ACP" testid="fa-caliber" />
            <Field label="Serial number" v={form.serial_no} set={(v) => setForm({ ...form, serial_no: v })} ph="RIA-123456" testid="fa-serial" mono />
            <div>
              <Label className="text-[10px] uppercase font-bold">Type</Label>
              <Select value={form.firearm_type} onValueChange={(v) => setForm({ ...form, firearm_type: v })}>
                <SelectTrigger data-testid="fa-type" className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Registration no." v={form.license_no} set={(v) => setForm({ ...form, license_no: v })} ph="LTOPF-…" testid="fa-license" mono />
            <div>
              <Label className="text-[10px] uppercase font-bold">Registration expires</Label>
              <Input type="date" data-testid="fa-expiry" className="rounded-none mt-1 h-9 font-mono"
                     value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} />
            </div>
            <Field label="Notes" v={form.notes} set={(v) => setForm({ ...form, notes: v })} testid="fa-notes" />
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="fa-submit"
                  disabled={!form.make || !form.model || !form.caliber || !form.serial_no} onClick={create}>
            ADD TO THE REGISTER
          </Button>
          <div className="text-[10px] text-muted-foreground text-center">
            The serial number is the unit&apos;s identity — one serial, one record. A duplicate is refused.
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- issue ---------------------------------------------------------- */}
      <Dialog open={!!issueFor} onOpenChange={(o) => !o && setIssueFor(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">
            Issue {issueFor?.make} {issueFor?.model} · <span className="font-mono">{issueFor?.serial_no}</span>
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-bold">Guard</Label>
              <Select value={issueForm.guard_id} onValueChange={(v) => setIssueForm({ ...issueForm, guard_id: v })}>
                <SelectTrigger data-testid="issue-guard" className="rounded-none mt-1 h-9"><SelectValue placeholder="Who is signing for it" /></SelectTrigger>
                <SelectContent>
                  {eligible.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name} — {g.position || "Security Guard"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">
                Only active, deployable guards who are not already signed for a firearm.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase font-bold">Rounds issued</Label>
                <Input type="number" min="0" data-testid="issue-rounds" className="rounded-none mt-1 h-9 font-mono"
                       value={issueForm.rounds} onChange={(e) => setIssueForm({ ...issueForm, rounds: e.target.value })} />
              </div>
              <Field label="Duty Detail Order" v={issueForm.duty_detail_no}
                     set={(v) => setIssueForm({ ...issueForm, duty_detail_no: v })} ph="DDO-2026-0001" testid="issue-ddo" mono />
            </div>
            <Field label="Remarks" v={issueForm.remarks} set={(v) => setIssueForm({ ...issueForm, remarks: v })} testid="issue-remarks" />
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="issue-submit"
                  disabled={!issueForm.guard_id} onClick={issue}>
            SIGN IT OUT
          </Button>
        </DialogContent>
      </Dialog>

      {/* ---- receive back --------------------------------------------------- */}
      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">
            Receive <span className="font-mono">{returnFor?.serial_no}</span> from {returnFor?.issued_to_name}
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase font-bold">Rounds issued</Label>
                <div className="h-9 flex items-center font-mono text-lg font-bold">{returnFor?.rounds_out ?? 0}</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold">Rounds returned</Label>
                <Input type="number" min="0" data-testid="return-rounds" className="rounded-none mt-1 h-9 font-mono"
                       value={returnForm.rounds_returned}
                       onChange={(e) => setReturnForm({ ...returnForm, rounds_returned: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold">Condition</Label>
              <Select value={returnForm.condition} onValueChange={(v) => setReturnForm({ ...returnForm, condition: v })}>
                <SelectTrigger data-testid="return-condition" className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {shortNow > 0 && (
              <div className="border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 space-y-2" data-testid="return-short">
                <div className="text-xs font-bold flex items-center gap-1.5">
                  <Warning size={15} weight="fill" className="text-[hsl(var(--alert))]" />
                  {shortNow} round(s) are unaccounted for
                </div>
                <div className="text-[11px] leading-snug text-muted-foreground">
                  Rounds that do not come back were fired, lost or taken, and each of those is a
                  different conversation. Say which — this is the record an inspection reads.
                </div>
                <Input data-testid="return-discrepancy" className="rounded-none h-9"
                       placeholder="e.g. 2 rounds expended at a live-fire requalification, range certificate filed"
                       value={returnForm.discrepancy_reason}
                       onChange={(e) => setReturnForm({ ...returnForm, discrepancy_reason: e.target.value })} />
              </div>
            )}
            <Field label="Remarks" v={returnForm.remarks} set={(v) => setReturnForm({ ...returnForm, remarks: v })} testid="return-remarks" />
          </div>
          <Button className="w-full rounded-none h-11 font-heading font-bold" data-testid="return-submit"
                  disabled={shortNow > 0 && !returnForm.discrepancy_reason.trim()} onClick={doReturn}>
            RECEIVE BACK
          </Button>
        </DialogContent>
      </Dialog>

      {/* ---- one unit's custody chain --------------------------------------- */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">
            {sel?.make} {sel?.model} · <span className="font-mono">{sel?.serial_no}</span>
          </DialogTitle></DialogHeader>
          {sel && (
            <>
              <div className="grid md:grid-cols-4 gap-3 mb-4 text-sm">
                <Kv k="Calibre" v={sel.caliber} mono />
                <Kv k="Type" v={sel.firearm_type} />
                <Kv k="Condition" v={sel.condition} />
                <Kv k="Registration" v={`${sel.license_no || "—"} · ${sel.license_expiry || "no expiry"}`} mono />
              </div>
              <h3 className="font-heading font-bold mb-2">Custody chain</h3>
              {!(sel.issuances || []).length && (
                <div className="border border-border border-dashed p-4 text-center text-xs text-muted-foreground">
                  This unit has never been signed out.
                </div>
              )}
              <div className="space-y-2">
                {[...(sel.issuances || [])].reverse().map((e) => (
                  <div key={e.id} className="border border-border p-3" data-testid={`chain-${e.id.slice(0, 8)}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{e.guard_name}</span>
                      <StatusBadge value={e.returned_at ? "returned" : "out"} />
                    </div>
                    <div className="grid md:grid-cols-4 gap-2 mt-2 text-[11px]">
                      <Kv k="Issued" v={`${e.issued_on} by ${e.issued_by}`} />
                      <Kv k="Returned" v={e.returned_on ? `${e.returned_on} to ${e.received_by}` : "still out"} />
                      <Kv k="Rounds" v={e.returned_at ? `${e.rounds_issued} out, ${e.rounds_returned} back` : `${e.rounds_issued} out`} mono />
                      <Kv k="Duty detail" v={e.duty_detail_no || "—"} mono />
                    </div>
                    {e.rounds_unaccounted > 0 && (
                      <div className="mt-2 text-[11px] border-l-2 border-[hsl(var(--alert))] pl-2">
                        <span className="font-bold text-[hsl(var(--alert))]">{e.rounds_unaccounted} round(s) unaccounted.</span>{" "}
                        {e.discrepancy_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-2xl tabular-nums">{value}</div>
    </div>
  );
}

function Kv({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{k}</div>
      <div className={mono ? "font-mono text-xs" : "text-xs"}>{v || "—"}</div>
    </div>
  );
}

function Field({ label, v, set, ph, testid, mono }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold">{label}</Label>
      <Input data-testid={testid} className={"rounded-none mt-1 h-9 " + (mono ? "font-mono" : "")}
             placeholder={ph} value={v} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
